import { useState, useEffect, useCallback, useMemo } from "react";
import type { ReactElement, ChangeEvent } from "react";
import { API_BASE_URL } from "../../../../config";
import { getAuthHeaders } from "../../../../api/auth";
import "./QualityCheckPage.css";
import "./QualityCheckPage_TableStyles.css";

interface YardDocument {
    _id: string;
    TRANSPORTCHARGE?: string | null;
    LKW_LOAD_ID?: string | null;
    WERK2?: string | null;
    KENNZEICHEN?: string | null;
    KENNZEICHEN_ANHAENGER?: string | null;
    LKW_LOAD_STATUS?: string | null; // SAP status "1"-"15"
    CHECKOUT_QS?: string | null; // SAP: Feld zum Ankreuzen (XFELD)
    LKW_ART?: string | null; // SAP: HB Lkw-Art (CHAR, 2)
    QUALITAET?: string | null; // SAP: HB Qualität (NUMC, 3)
    QS_CODE?: string | null; // SAP: Code (CHAR, 4)
    ABLADEORT_ART?: string | null; // SAP: HB Kz Produktion/Lager (CHAR, 1)
    unloadingType?: string | null;
    weightInbound?: number | null;
    WAAGENNR_EINFAHRT?: string | null; // SAP: Waagenummer Einfahrt (NUMC, 1)
    weightOutbound?: number | null;
    WAAGENNR_AUSFAHRT?: string | null; // SAP: Waagenummer Ausfahrt (NUMC, 1)
    FGRAD?: number | null; // SAP: HB Lkw-Füllgrad (DEC, 3, 0)
    hasTrailer?: string | null; // Frontend-only: Hat Anhänger (XFELD)
    LAENGE_ANH?: string | null; // SAP: HB Lkw-Länge Anhänger (QUAN, 7, 2)
    BREITE_ANH?: string | null; // SAP: HB Lkw-Breite Anhänger (QUAN, 7, 2)
    HOEHE_ANH?: string | null; // SAP: HB Lkw-Höhe Anhänger (QUAN, 7, 2)
    FGRAD_ANH?: number | null; // SAP: HB Lkw-Füllgrad Anhänger (DEC, 3, 0)
    LIEFERANT?: string | null; // SAP supplier field
    SORTIMENT?: string | null; // SAP assortment field
    SPEDITION?: string | null; // SAP forwarder field
    Contract?: string | null; // SAP contract field (uppercase)
    FRAKTION?: string | null; // SAP fraction field
    loadingLocationName?: string | null;
    stagingNumber?: string | null;
    stagingNumber2?: string | null;
    stagingNumber3?: string | null;
    stagingNumber4?: string | null;
    stagingNumber5?: string | null;
    assortmentPos1?: string | null;
    distributionPos1?: number | null;
    assortmentPos2?: string | null;
    distributionPos2?: number | null;
    assortmentPos3?: string | null;
    distributionPos3?: number | null;
    assortmentPos4?: string | null;
    distributionPos4?: number | null;
    assortmentPos5?: string | null;
    distributionPos5?: number | null;
    stagingFinishedFlagPos1?: boolean | null;
    stagingFinishedFlagPos2?: boolean | null;
    stagingFinishedFlagPos3?: boolean | null;
    stagingFinishedFlagPos4?: boolean | null;
    stagingFinishedFlagPos5?: boolean | null;
    QANOTE1?: string | null; // SAP QA note fields
    QANOTE2?: string | null;
    QANOTE3?: string | null;
    QANOTE4?: string | null;
    QANOTE5?: string | null;
    // Legacy fields for backward compatibility
    transportBatchNumber?: string | null;
    transportNumber?: string | null;
    plantCode?: string | null;
    truckLicensePlate?: string | null;
    status?: number | null;
    supplierName?: string | null;
    assortmentName?: string | null;
    forwarder?: string | null;
    contract?: string | null; // Legacy lowercase contract
    fraction?: string | null;
    qaNote1?: string | null;
    qaNote2?: string | null;
    qaNote3?: string | null;
    qaNote4?: string | null;
    qaNote5?: string | null;
}

interface EditableFields {
    CHECKOUT_QS: boolean; // SAP: Feld zum Ankreuzen
    LKW_ART: string; // SAP: HB Lkw-Art (CHAR, 2)
    QUALITAET: string; // SAP: HB Qualität (NUMC, 3)
    QS_CODE: string; // SAP: Code (CHAR, 4)
    ABLADEORT_ART: string; // SAP: HB Kz Produktion/Lager (CHAR, 1)
    KENNZEICHEN: string; // SAP: KFZ-Kennzeichen (CHAR, 12)
    weightInbound: string;
    WAAGENNR_EINFAHRT: string; // SAP: Waagenummer (NUMC, 1)
    WAAGENNR_AUSFAHRT: string; // SAP: Waagenummer (NUMC, 1)
    FGRAD: number; // SAP: HB Lkw-Füllgrad (DEC, 3, 0)
    hasTrailer: boolean; // Frontend: Hat Anhänger (wird zu XFELD)
    KENNZEICHEN_ANHAENGER: string; // SAP: KFZ-Kennzeichen Anhänger (CHAR, 12)
    LAENGE_ANH: string; // SAP: HB Lkw-Länge Anhänger (QUAN, 7, 2)
    BREITE_ANH: string; // SAP: HB Lkw-Breite Anhänger (QUAN, 7, 2)
    HOEHE_ANH: string; // SAP: HB Lkw-Höhe Anhänger (QUAN, 7, 2)
    FGRAD_ANH: number; // SAP: HB Lkw-Füllgrad Anhänger (DEC, 3, 0)
}

interface StagingEntry {
    position: number; // 1-5
    stagingNumber: string;
    assortment: string;
    distribution: number;
    isCompleted: boolean;
}

const PLANTS = [
    { id: "alle", name: "Beide" },
    { id: "0123", name: "Werk 0123" },
    { id: "0128", name: "Werk 0128" },
];

const UNLOADING_TYPES = [
    { id: "alle", name: "Beide" },
    { id: "Zerspaner", name: "Zerspaner" },
    { id: "Schüttgut", name: "Schüttgut" },
];

/**
 * STATUS FILTER CONFIGURATION
 * 
 * These status filters are automatically applied to backend requests.
 * They define which statuses appear in each section.
 * 
 * Status mapping:
 * 1 = Created, 2 = Checked In, 3-8 = On Yard/Processing, 
 * 10-11 = Warehouse, 15 = Checked Out
 */

// Statuses for "On Yard" section (Status 3-8)
const ON_YARD_STATUSES = [3, 4, 5, 6, 7, 8];

// Statuses for "Warehouse Transports" section (Status 10-11)
const WAREHOUSE_STATUSES = [10, 11];

export function QualityCheckPage(): ReactElement {
    const [selectedPlant, setSelectedPlant] = useState<string>(() => {
        // Lade aus LocalStorage oder nutze Default
        return localStorage.getItem("selectedPlant_quality") || "0123";
    });
    const [selectedUnloadingType, setSelectedUnloadingType] = useState<string>(() => {
        return localStorage.getItem("selectedUnloadingType_quality") || "alle";
    });
    const [onYardDocuments, setOnYardDocuments] = useState<YardDocument[]>([]);
    const [warehouseDocuments, setWarehouseDocuments] = useState<YardDocument[]>([]);
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
    const [stagingEntries, setStagingEntries] = useState<StagingEntry[]>([]);
    const [editableFields, setEditableFields] = useState<EditableFields>({
        CHECKOUT_QS: false,
        LKW_ART: "",
        QUALITAET: "",
        QS_CODE: "",
        ABLADEORT_ART: "",
        KENNZEICHEN: "",
        weightInbound: "",
        WAAGENNR_EINFAHRT: "",
        WAAGENNR_AUSFAHRT: "",
        FGRAD: 0,
        hasTrailer: false,
        KENNZEICHEN_ANHAENGER: "",
        LAENGE_ANH: "",
        BREITE_ANH: "",
        HOEHE_ANH: "",
        FGRAD_ANH: 0
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);
    const [qaNotes, setQaNotes] = useState<string[]>([]); // Max 5 Notizen
    const [newNoteInput, setNewNoteInput] = useState<string>("");

    // Fetch YardDocuments
    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Build query parameters for backend filtering
            const queryParams = new URLSearchParams();

            // Add plant filter
            // IMPORTANT: "alle" means both plants, not ALL plants in SAP (which would be too large)
            // Always specify plant filters explicitly to avoid loading entire ZHB_T_LKW_LOAD table
            if (selectedPlant === "alle") {
                // "Beide" = explicitly filter for both plants (0123 and 0128)
                queryParams.append("plant", "0123");
                queryParams.append("plant", "0128");
            } else {
                // Single plant selected
                queryParams.append("plant", selectedPlant);
            }

            // Add unloading type filter (unless "alle" is selected)
            if (selectedUnloadingType !== "alle") {
                queryParams.append("unloadingType", selectedUnloadingType);
            }

            // Add status filters (always applied, not configurable by user)
            // Request both On Yard (3-8) and Warehouse (10-11) statuses
            const allStatuses = [...ON_YARD_STATUSES, ...WAREHOUSE_STATUSES];
            allStatuses.forEach(status => {
                queryParams.append("status", status.toString());
            });

            const url = `${API_BASE_URL}/api/yard-documents${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

            const response = await fetch(url, {
                headers: getAuthHeaders(),
                credentials: "include"
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            // Handle different response formats
            let items: YardDocument[] = [];
            if (Array.isArray(data)) {
                items = data;
            } else if (Array.isArray(data.items)) {
                items = data.items;
            } else if (Array.isArray(data.data)) {
                items = data.data;
            }

            // Filter by selected plant first (client-side fallback)
            const plantFiltered = items.filter((doc: YardDocument) => {
                // "alle" zeigt beide Werke
                if (selectedPlant === "alle") {
                    const plantCode = doc.WERK2 ?? doc.plantCode; // Try SAP field first
                    return plantCode === "0123" || plantCode === "0128";
                }
                const plantCode = doc.WERK2 ?? doc.plantCode;
                return plantCode === selectedPlant;
            });

            // Filter by unloadingType (client-side fallback)
            const typeFiltered = plantFiltered.filter((doc: YardDocument) => {
                if (selectedUnloadingType === "alle") {
                    return true; // Zeige alle
                }
                return doc.unloadingType === selectedUnloadingType;
            });

            // Helper function to get status as number
            const getStatus = (doc: YardDocument): number => {
                const statusStr = doc.LKW_LOAD_STATUS ?? String(doc.status ?? 0);
                return parseInt(statusStr, 10);
            };

            // Filter: On Yard (Status 3-8) - client-side fallback
            const onYard = typeFiltered.filter((doc: YardDocument) => {
                const status = getStatus(doc);
                return ON_YARD_STATUSES.includes(status);
            });

            // Filter: Warehouse Transports (Status 10-11) - client-side fallback
            const warehouse = typeFiltered.filter((doc: YardDocument) => {
                const status = getStatus(doc);
                return WAREHOUSE_STATUSES.includes(status);
            });

            // Sortiere beide Listen aufsteigend nach transportNumber
            const sortByTransportNumber = (a: YardDocument, b: YardDocument) => {
                const numA = a.transportNumber ?? "";
                const numB = b.transportNumber ?? "";
                return numA.localeCompare(numB, undefined, { numeric: true });
            };

            onYard.sort(sortByTransportNumber);
            warehouse.sort(sortByTransportNumber);

            setOnYardDocuments(onYard);
            setWarehouseDocuments(warehouse);

            // Auto-select erstes Dokument nur beim ersten Laden (wenn noch nichts ausgewählt ist)
            if (!selectedDocumentId) {
                if (onYard.length > 0) {
                    setSelectedDocumentId(onYard[0]._id);
                } else if (warehouse.length > 0) {
                    setSelectedDocumentId(warehouse[0]._id);
                }
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Fehler beim Laden der Daten";
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [selectedPlant, selectedUnloadingType, selectedDocumentId]);

    useEffect(() => {
        void fetchDocuments();
    }, [fetchDocuments]);

    // Update editableFields und stagingEntries NUR wenn sich das selectedDocumentId ändert
    useEffect(() => {
        if (!selectedDocumentId) {
            return;
        }

        // Find document directly here to avoid dependency issues
        const allDocs = [...onYardDocuments, ...warehouseDocuments];
        const selectedDoc = allDocs.find(doc => doc._id === selectedDocumentId);

        if (!selectedDoc) {
            return;
        }

        setEditableFields({
            CHECKOUT_QS: selectedDoc.CHECKOUT_QS === "X",
            LKW_ART: selectedDoc.LKW_ART ?? "",
            QUALITAET: selectedDoc.QUALITAET ?? "001", // Vorbelegen mit "001"
            QS_CODE: selectedDoc.QS_CODE ?? "",
            ABLADEORT_ART: selectedDoc.ABLADEORT_ART ?? "P", // Vorbelegen mit "P"
            KENNZEICHEN: selectedDoc.KENNZEICHEN ?? "",
            weightInbound: selectedDoc.weightInbound?.toString() ?? "",
            WAAGENNR_EINFAHRT: selectedDoc.WAAGENNR_EINFAHRT ?? "",
            WAAGENNR_AUSFAHRT: selectedDoc.WAAGENNR_AUSFAHRT ?? "",
            FGRAD: selectedDoc.FGRAD ?? 0,
            hasTrailer: selectedDoc.hasTrailer === "X",
            KENNZEICHEN_ANHAENGER: selectedDoc.KENNZEICHEN_ANHAENGER ?? "",
            LAENGE_ANH: selectedDoc.LAENGE_ANH ?? "",
            BREITE_ANH: selectedDoc.BREITE_ANH ?? "",
            HOEHE_ANH: selectedDoc.HOEHE_ANH ?? "",
            FGRAD_ANH: selectedDoc.FGRAD_ANH ?? 0
        });

        // Load staging entries from document
        const loadedEntries: StagingEntry[] = [];
        const stagingNumbers = [
            selectedDoc.stagingNumber,
            selectedDoc.stagingNumber2,
            selectedDoc.stagingNumber3,
            selectedDoc.stagingNumber4,
            selectedDoc.stagingNumber5
        ];
        const assortments = [
            selectedDoc.assortmentPos1,
            selectedDoc.assortmentPos2,
            selectedDoc.assortmentPos3,
            selectedDoc.assortmentPos4,
            selectedDoc.assortmentPos5
        ];
        const distributions = [
            selectedDoc.distributionPos1,
            selectedDoc.distributionPos2,
            selectedDoc.distributionPos3,
            selectedDoc.distributionPos4,
            selectedDoc.distributionPos5
        ];
        const finishedFlags = [
            selectedDoc.stagingFinishedFlagPos1,
            selectedDoc.stagingFinishedFlagPos2,
            selectedDoc.stagingFinishedFlagPos3,
            selectedDoc.stagingFinishedFlagPos4,
            selectedDoc.stagingFinishedFlagPos5
        ];

        for (let i = 0; i < 5; i++) {
            if (stagingNumbers[i] || assortments[i]) {
                const entry = {
                    position: i + 1,
                    stagingNumber: stagingNumbers[i] ?? "",
                    assortment: assortments[i] ?? "",
                    distribution: distributions[i] ?? 0,
                    isCompleted: finishedFlags[i] === true
                };
                loadedEntries.push(entry);
            }
        }
        setStagingEntries(loadedEntries);

        // Load QA Notes from document
        const loadedNotes: string[] = [];
        const noteFields = [
            selectedDoc.QANOTE1 ?? selectedDoc.qaNote1, // Try SAP field first
            selectedDoc.QANOTE2 ?? selectedDoc.qaNote2,
            selectedDoc.QANOTE3 ?? selectedDoc.qaNote3,
            selectedDoc.QANOTE4 ?? selectedDoc.qaNote4,
            selectedDoc.QANOTE5 ?? selectedDoc.qaNote5
        ];

        noteFields.forEach(note => {
            if (note) {
                loadedNotes.push(note);
            }
        });
        setQaNotes(loadedNotes);
        setNewNoteInput("");
    }, [selectedDocumentId]); // NUR selectedDocumentId als Dependency!
    // WICHTIG: onYardDocuments und warehouseDocuments NICHT als Dependencies,
    // sonst wird der Effect bei jedem fetchDocuments() neu ausgelöst!

    // Memoize selected document for rendering (READ-ONLY, not for effects!)
    const selectedDocument = useMemo(() => {
        if (!selectedDocumentId) return null;
        const allDocs = [...onYardDocuments, ...warehouseDocuments];
        return allDocs.find(doc => doc._id === selectedDocumentId) ?? null;
    }, [selectedDocumentId, onYardDocuments, warehouseDocuments]);

    // Handle field change
    const handleFieldChange = useCallback((field: keyof EditableFields, value: string | boolean | number) => {
        setEditableFields(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // Staging table handlers
    const addStagingEntry = useCallback(() => {
        if (stagingEntries.length >= 5) {
            return; // Maximal 5 Einträge
        }
        const nextPosition = stagingEntries.length + 1;
        const newEntry: StagingEntry = {
            position: nextPosition,
            stagingNumber: "",
            assortment: "",
            distribution: 5,
            isCompleted: false
        };
        setStagingEntries(prev => [...prev, newEntry]);
    }, [stagingEntries.length]);

    const updateStagingEntry = useCallback((position: number, field: keyof Omit<StagingEntry, 'position'>, value: string | number | boolean) => {
        setStagingEntries(prev => {
            const updated = prev.map(entry =>
                entry.position === position ? { ...entry, [field]: value } : entry
            );
            return updated;
        });
    }, []);

    // Add new note with rotation logic (max 5 notes, max 70 characters)
    const handleAddNote = useCallback(() => {
        const trimmedNote = newNoteInput.trim();
        if (!trimmedNote) return;

        // Validate max length of 70 characters
        if (trimmedNote.length > 70) {
            return; // Silently ignore if too long
        }

        setQaNotes(prev => {
            const updatedNotes = [...prev];
            if (updatedNotes.length >= 5) {
                // Rotate: remove oldest (first), add newest at end
                updatedNotes.shift();
            }
            updatedNotes.push(trimmedNote);
            return updatedNotes;
        });
        setNewNoteInput("");
    }, [newNoteInput]);

    // Delete a specific note (only in state, not saved until user clicks Save)
    const handleDeleteNote = useCallback((indexToDelete: number) => {
        setQaNotes(prev => prev.filter((_, index) => index !== indexToDelete));
    }, []);


    const deleteStagingEntry = useCallback((position: number) => {
        setStagingEntries(prev => {
            const filtered = prev.filter(entry => entry.position !== position);
            // Re-nummeriere die Positionen
            return filtered.map((entry, index) => ({
                ...entry,
                position: index + 1
            }));
        });
    }, []);

    // Format license plate: uppercase, no special chars, no spaces
    const formatLicensePlate = (plate: string): string => {
        return plate
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, "");
    };

    // Handle save
    const handleSave = useCallback(async () => {
        if (!selectedDocumentId) {
            return;
        }

        // Pflichtfelder-Validierung
        const requiredFields: Array<{ field: keyof EditableFields; label: string }> = [
            { field: "LKW_ART", label: "LKW-Typ" },
            { field: "QUALITAET", label: "Qualität" },
            { field: "ABLADEORT_ART", label: "Abladeort-Typ" },
            { field: "KENNZEICHEN", label: "Kennzeichen" }
        ];

        const missingFields = requiredFields.filter(({ field }) => {
            const value = editableFields[field];
            return !value || (typeof value === "string" && value.trim() === "");
        });

        if (missingFields.length > 0) {
            const fieldLabels = missingFields.map(f => f.label).join(", ");
            setSaveMessage(`Fehler: Folgende Pflichtfelder müssen ausgefüllt werden: ${fieldLabels}`);
            setTimeout(() => setSaveMessage(null), 5000);
            return;
        }

        // Validierung: Keine Bereitstellung mit 0% erlaubt
        const hasZeroDistribution = stagingEntries.some(entry => entry.distribution === 0);
        if (hasZeroDistribution) {
            setSaveMessage("Fehler: Bereitstellungen dürfen keine 0% Verteilung haben. Mindestens 5% erforderlich.");
            setTimeout(() => setSaveMessage(null), 5000);
            return;
        }

        // Validierung für Waldholz: Verteilung muss 100% ergeben
        const allDocs = [...onYardDocuments, ...warehouseDocuments];
        const currentDoc = allDocs.find(doc => doc._id === selectedDocumentId);

        if (currentDoc?.fraction?.toUpperCase() === "WALDHOLZ") {
            const totalDistribution = stagingEntries.reduce((sum, entry) => sum + (entry.distribution || 0), 0);

            if (stagingEntries.length > 0 && totalDistribution !== 100) {
                setSaveMessage(`Fehler: Die Verteilung muss insgesamt 100% ergeben (aktuell: ${totalDistribution}%)`);
                setTimeout(() => setSaveMessage(null), 5000);
                return;
            }
        }

        try {
            // Prepare staging data
            const stagingData: Record<string, string | number | boolean | null> = {
                stagingNumber: null,
                stagingNumber2: null,
                stagingNumber3: null,
                stagingNumber4: null,
                stagingNumber5: null,
                assortmentPos1: null,
                distributionPos1: null,
                assortmentPos2: null,
                distributionPos2: null,
                assortmentPos3: null,
                distributionPos3: null,
                assortmentPos4: null,
                distributionPos4: null,
                assortmentPos5: null,
                distributionPos5: null,
                stagingFinishedFlagPos1: null,
                stagingFinishedFlagPos2: null,
                stagingFinishedFlagPos3: null,
                stagingFinishedFlagPos4: null,
                stagingFinishedFlagPos5: null
            };

            stagingEntries.forEach((entry) => {
                const pos = entry.position;

                if (pos === 1) {
                    stagingData.stagingNumber = entry.stagingNumber || null;
                    stagingData.assortmentPos1 = entry.assortment || null;
                    stagingData.distributionPos1 = entry.distribution;
                    stagingData.stagingFinishedFlagPos1 = entry.isCompleted ? true : null;
                } else if (pos === 2) {
                    stagingData.stagingNumber2 = entry.stagingNumber || null;
                    stagingData.assortmentPos2 = entry.assortment || null;
                    stagingData.distributionPos2 = entry.distribution;
                    stagingData.stagingFinishedFlagPos2 = entry.isCompleted ? true : null;
                } else if (pos === 3) {
                    stagingData.stagingNumber3 = entry.stagingNumber || null;
                    stagingData.assortmentPos3 = entry.assortment || null;
                    stagingData.distributionPos3 = entry.distribution;
                    stagingData.stagingFinishedFlagPos3 = entry.isCompleted ? true : null;
                } else if (pos === 4) {
                    stagingData.stagingNumber4 = entry.stagingNumber || null;
                    stagingData.assortmentPos4 = entry.assortment || null;
                    stagingData.distributionPos4 = entry.distribution;
                    stagingData.stagingFinishedFlagPos4 = entry.isCompleted ? true : null;
                } else if (pos === 5) {
                    stagingData.stagingNumber5 = entry.stagingNumber || null;
                    stagingData.assortmentPos5 = entry.assortment || null;
                    stagingData.distributionPos5 = entry.distribution;
                    stagingData.stagingFinishedFlagPos5 = entry.isCompleted ? true : null;
                }
            });

            // Map qaNotes array to QANOTE1-5 properties (SAP fields)
            const qaNotesData: Record<string, string | null> = {
                QANOTE1: qaNotes[0] || null,
                QANOTE2: qaNotes[1] || null,
                QANOTE3: qaNotes[2] || null,
                QANOTE4: qaNotes[3] || null,
                QANOTE5: qaNotes[4] || null
            };

            const payload: Record<string, unknown> = {
                LKW_LOAD_STATUS: "6", // SAP status as string
                CHECKOUT_QS: editableFields.CHECKOUT_QS ? "X" : null,
                LKW_ART: editableFields.LKW_ART || null,
                QUALITAET: editableFields.QUALITAET || null,
                QS_CODE: editableFields.QS_CODE || null,
                ABLADEORT_ART: editableFields.ABLADEORT_ART || null,
                KENNZEICHEN: editableFields.KENNZEICHEN
                    ? formatLicensePlate(editableFields.KENNZEICHEN)
                    : null,
                WAAGENNR_EINFAHRT: editableFields.WAAGENNR_EINFAHRT || null,
                WAAGENNR_AUSFAHRT: editableFields.WAAGENNR_AUSFAHRT || null,
                FGRAD: editableFields.FGRAD,
                hasTrailer: editableFields.hasTrailer ? "X" : null,
                KENNZEICHEN_ANHAENGER: editableFields.KENNZEICHEN_ANHAENGER
                    ? formatLicensePlate(editableFields.KENNZEICHEN_ANHAENGER)
                    : null,
                LAENGE_ANH: editableFields.LAENGE_ANH ? parseFloat(editableFields.LAENGE_ANH) : null,
                BREITE_ANH: editableFields.BREITE_ANH ? parseFloat(editableFields.BREITE_ANH) : null,
                HOEHE_ANH: editableFields.HOEHE_ANH ? parseFloat(editableFields.HOEHE_ANH) : null,
                FGRAD_ANH: editableFields.FGRAD_ANH,
                ...stagingData,
                ...qaNotesData
            };

            const response = await fetch(`${API_BASE_URL}/api/yard-documents/${selectedDocumentId}`, {
                method: "PUT",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json"
                },
                credentials: "include",
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            setSaveMessage("Erfolgreich gespeichert");
            setTimeout(() => setSaveMessage(null), 3000);

            // Refresh data
            void fetchDocuments();
        } catch (err) {
            const message = err instanceof Error ? err.message : "Fehler beim Speichern";
            setSaveMessage(`Fehler: ${message}`);
            setTimeout(() => setSaveMessage(null), 5000);
        }
    }, [selectedDocumentId, editableFields, stagingEntries, qaNotes, onYardDocuments, warehouseDocuments, fetchDocuments]);

    // Handle document selection
    const handleDocumentClick = useCallback((docId: string) => {
        setSelectedDocumentId(docId);
    }, []);

    // Handle plant change
    const handlePlantChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPlant = event.target.value;
        setSelectedPlant(newPlant);
        localStorage.setItem("selectedPlant_quality", newPlant);
        // Reset selected document when switching plants
        setSelectedDocumentId(null);
        // Data will reload automatically via useEffect dependency on selectedPlant
    }, []);

    // Handle unloading type change
    const handleUnloadingTypeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = event.target.value;
        setSelectedUnloadingType(newType);
        localStorage.setItem("selectedUnloadingType_quality", newType);
        // Reset selected document when switching types
        setSelectedDocumentId(null);
        // Data will reload automatically via useEffect dependency on selectedUnloadingType
    }, []);

    // selectedDocument wird bereits oben via useMemo berechnet

    return (
        <div className="quality-check-container">
            {saveMessage && (
                <div className={`quality-save-notification ${saveMessage.startsWith("Fehler") ? "error" : "success"}`}>
                    {saveMessage}
                </div>
            )}

            <div className="quality-check-layout">
                {/* Linke Liste (15%) */}
                <aside className="quality-check-sidebar">
                    <div className="quality-sidebar-plant-selector">
                        <select
                            value={selectedPlant}
                            onChange={handlePlantChange}
                            disabled={loading}
                            className="quality-plant-select"
                        >
                            {PLANTS.map((plant) => (
                                <option key={plant.id} value={plant.id}>
                                    {plant.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="quality-sidebar-plant-selector">
                        <select
                            value={selectedUnloadingType}
                            onChange={handleUnloadingTypeChange}
                            disabled={loading}
                            className="quality-plant-select"
                        >
                            {UNLOADING_TYPES.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="quality-check-sidebar-header">
                        <h2>Transporte</h2>
                        <button
                            type="button"
                            className="quality-refresh-btn"
                            onClick={() => fetchDocuments()}
                            disabled={loading}
                        >
                            ↻
                        </button>
                    </div>

                    {loading && (
                        <div className="quality-loading-state">
                            <span className="quality-spinner" />
                            <p>Lade...</p>
                        </div>
                    )}

                    {error && (
                        <div className="quality-error-state">
                            <p>⚠️ {error}</p>
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="quality-list-groups">
                            {/* On Yard Group - Status 3-8 */}
                            <div className="quality-list-group">
                                <h3 className="quality-group-title">On Yard</h3>
                                <ul className="quality-document-list">
                                    {onYardDocuments.map(doc => (
                                        <li
                                            key={doc._id}
                                            className={`quality-document-item ${selectedDocumentId === doc._id ? "active" : ""}`}
                                            onClick={() => handleDocumentClick(doc._id)}
                                        >
                                            <div className="quality-item-number">
                                                {doc.LKW_LOAD_ID ?? doc.TRANSPORTCHARGE ?? "—"}
                                            </div>
                                            <div className="quality-item-plate">
                                                {doc.KENNZEICHEN ?? "—"}
                                            </div>
                                            <div className="quality-item-supplier">
                                                {doc.LIEFERANT ?? doc.supplierName ?? "—"}
                                            </div>
                                        </li>
                                    ))}
                                    {onYardDocuments.length === 0 && (
                                        <li className="quality-empty-item">Keine Transporte</li>
                                    )}
                                </ul>
                            </div>

                            {/* Warehouse Transports Group - Status 10-11 */}
                            <div className="quality-list-group">
                                <h3 className="quality-group-title">Warehouse Transports</h3>
                                <ul className="quality-document-list">
                                    {warehouseDocuments.map(doc => (
                                        <li
                                            key={doc._id}
                                            className={`quality-document-item ${selectedDocumentId === doc._id ? "active" : ""}`}
                                            onClick={() => handleDocumentClick(doc._id)}
                                        >
                                            <div className="quality-item-number">
                                                {doc.LKW_LOAD_ID ?? doc.TRANSPORTCHARGE ?? "—"}
                                            </div>
                                            <div className="quality-item-plate">
                                                {doc.KENNZEICHEN ?? "—"}
                                            </div>
                                            <div className="quality-item-supplier">
                                                {doc.LIEFERANT ?? doc.supplierName ?? "—"}
                                            </div>
                                        </li>
                                    ))}
                                    {warehouseDocuments.length === 0 && (
                                        <li className="quality-empty-item">Keine Transporte</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}
                </aside>

                {/* Rechte Detailansicht (85%) */}
                <main className="quality-check-content">
                    {selectedDocument ? (
                        <>
                            <header className="quality-content-header">
                                <div>
                                    <h1 className="quality-content-title">Qualitätsprüfung</h1>
                                    <p className="quality-content-subtitle">
                                        Transport: {selectedDocument.LKW_LOAD_ID ?? selectedDocument.TRANSPORTCHARGE ?? "Unbekannt"}
                                    </p>
                                    <div className="quality-details-grid">
                                        <p className="quality-content-subtitle">
                                            Lieferant: {selectedDocument.LIEFERANT ?? "-"}
                                        </p>
                                        <p className="quality-content-subtitle">
                                            Sortiment: {selectedDocument.SORTIMENT ?? "-"}
                                        </p>
                                        <p className="quality-content-subtitle">
                                            Gewicht Check-In (t): {selectedDocument.weightInbound != null ? selectedDocument.weightInbound.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                                        </p>
                                        <p className="quality-content-subtitle">
                                            Spediteur: {selectedDocument.SPEDITION ?? "-"}
                                        </p>
                                        <p className="quality-content-subtitle">
                                            Vertrag: {selectedDocument.Contract ?? selectedDocument.contract ?? "-"}
                                        </p>
                                        <p className="quality-content-subtitle">
                                            Fraktion: {selectedDocument.FRAKTION ?? "-"}
                                        </p>
                                        <p className="quality-content-subtitle">
                                            Ladeort: {selectedDocument.loadingLocationName ?? "-"}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="quality-save-btn"
                                    onClick={handleSave}
                                >
                                    Speichern
                                </button>
                            </header>

                            <div className="quality-form">
                                {/* Checkout Status - Prominente Darstellung */}
                                <div className="quality-checkout-status-card">
                                    <button
                                        type="button"
                                        className={`quality-checkout-toggle ${editableFields.CHECKOUT_QS ? "blocked" : "released"}`}
                                        onClick={() => handleFieldChange("CHECKOUT_QS", !editableFields.CHECKOUT_QS)}
                                    >
                                        <span className="quality-checkout-icon">
                                            {editableFields.CHECKOUT_QS ? "🔒" : "✓"}
                                        </span>
                                        <span className="quality-checkout-text">
                                            {editableFields.CHECKOUT_QS
                                                ? "Für Checkout gesperrt"
                                                : "Für Checkout freigegeben"}
                                        </span>
                                    </button>
                                    <div className="quality-checkout-hint">
                                        {editableFields.CHECKOUT_QS
                                            ? "LKW kann das Werk nicht verlassen (Qualitätsprüfung erforderlich)"
                                            : "LKW kann ausgecheckt werden"}
                                    </div>
                                </div>

                                {/* Kachel-Grid für Formular-Felder */}
                                <div className="quality-tile-grid">
                                    {/* LKW-Typ */}
                                    <div className="quality-tile">
                                        <label className="quality-tile-label">LKW-Typ</label>
                                        <select
                                            className="quality-tile-input"
                                            value={editableFields.LKW_ART}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                handleFieldChange("LKW_ART", e.target.value)
                                            }
                                        >
                                            <option value="">-- Bitte wählen --</option>
                                            <option value="01">01 - Schubboden</option>
                                            <option value="02">02 - Seitenkipper</option>
                                            <option value="03">03 - Container</option>
                                            <option value="04">04 - Saugzug</option>
                                            <option value="05">05 - Rungenfahrzeug</option>
                                            <option value="06">06 - Kipper</option>
                                            <option value="07">07 - Traktor mit Anhänger</option>
                                            <option value="08">08 - Waggon</option>
                                            <option value="09">09 - Stapler</option>
                                            <option value="10">10 - Radlader</option>
                                            <option value="11">11 - Planenzug</option>
                                            <option value="99">99 - ohne LKW-Art</option>
                                        </select>
                                    </div>

                                    {/* Qualität */}
                                    <div className="quality-tile">
                                        <label className="quality-tile-label">Qualität</label>
                                        <select
                                            className="quality-tile-input"
                                            value={editableFields.QUALITAET}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                handleFieldChange("QUALITAET", e.target.value)
                                            }
                                        >
                                            <option value="">-- Bitte wählen --</option>
                                            <option value="001">001</option>
                                            <option value="002">002</option>
                                            <option value="003">003</option>
                                        </select>

                                        {/* QS Code - nur wenn Qualität 002 oder 003 */}
                                        {(editableFields.QUALITAET === "002" || editableFields.QUALITAET === "003") && (
                                            <>
                                                <label className="quality-tile-label" style={{ marginTop: '8px' }}>QS Code</label>
                                                <select
                                                    className="quality-tile-input"
                                                    value={editableFields.QS_CODE}
                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                        handleFieldChange("QS_CODE", e.target.value)
                                                    }
                                                >
                                                    <option value="">-- Bitte wählen --</option>
                                                    <option value="W011">W011 - Dimension</option>
                                                    <option value="W012">W012 - Holzart</option>
                                                    <option value="W013">W013 - Astigkeit</option>
                                                    <option value="W014">W014 - Krümmung</option>
                                                    <option value="W015">W015 - Holzfäule</option>
                                                    <option value="W016">W016 - Rinde</option>
                                                    <option value="W017">W017 - Mineralverschmutzung</option>
                                                    <option value="W018">W018 - Andere Verschmutzung</option>
                                                    <option value="W019">W019 - Verschmutzung mit Störstoffen</option>
                                                    <option value="W020">W020 - Staubgehalt</option>
                                                    <option value="W021">W021 - Fraktion</option>
                                                    <option value="W022">W022 - Nicht-Holz Teile</option>
                                                </select>
                                            </>
                                        )}
                                    </div>

                                    {/* Abladeort-Typ */}
                                    <div className="quality-tile">
                                        <label className="quality-tile-label">Abladeort-Typ</label>
                                        <select
                                            className="quality-tile-input"
                                            value={editableFields.ABLADEORT_ART}
                                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                handleFieldChange("ABLADEORT_ART", e.target.value)
                                            }
                                        >
                                            <option value="">-- Bitte wählen --</option>
                                            <option value="P">P</option>
                                            <option value="L">L</option>
                                        </select>
                                    </div>

                                    {/* LKW-Kennzeichen */}
                                    <div className="quality-tile">
                                        <label className="quality-tile-label">LKW-Kennzeichen</label>
                                        <input
                                            type="text"
                                            className="quality-tile-input"
                                            value={editableFields.KENNZEICHEN}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFieldChange("KENNZEICHEN", e.target.value)
                                            }
                                        />
                                    </div>

                                    {/* LKW Füllgrad - visuelle Kachel */}
                                    <div className="quality-tile quality-tile-fillgrade">
                                        <label className="quality-tile-label">LKW Füllgrad (cm)</label>
                                        <div className="quality-fillgrade-display">
                                            <span className="quality-fillgrade-value">{editableFields.FGRAD} cm</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="-200"
                                            max="60"
                                            step="5"
                                            className="quality-fillgrade-slider"
                                            value={editableFields.FGRAD}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                handleFieldChange("FGRAD", parseInt(e.target.value, 10))
                                            }
                                        />
                                    </div>

                                    {/* Anhänger vorhanden - Toggle-Kachel */}
                                    <div className="quality-tile quality-tile-toggle">
                                        <label className="quality-tile-label">Anhänger vorhanden?</label>
                                        <button
                                            type="button"
                                            className={`quality-toggle-button ${editableFields.hasTrailer ? "active" : ""}`}
                                            onClick={() => handleFieldChange("hasTrailer", !editableFields.hasTrailer)}
                                        >
                                            <span className="quality-toggle-option">{editableFields.hasTrailer ? "JA" : "NEIN"}</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Anhänger-Felder in Kacheln */}
                                {editableFields.hasTrailer && (
                                    <div className="quality-tile-grid">
                                        <div className="quality-tile">
                                            <label className="quality-tile-label">Anhänger Kennzeichen</label>
                                            <input
                                                type="text"
                                                className="quality-tile-input"
                                                value={editableFields.KENNZEICHEN_ANHAENGER}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    handleFieldChange("KENNZEICHEN_ANHAENGER", e.target.value)
                                                }
                                            />
                                        </div>

                                        <div className="quality-tile">
                                            <label className="quality-tile-label">Anhänger Länge (m)</label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                className="quality-tile-input"
                                                value={editableFields.LAENGE_ANH}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    handleFieldChange("LAENGE_ANH", e.target.value)
                                                }
                                            />
                                        </div>

                                        <div className="quality-tile">
                                            <label className="quality-tile-label">Anhänger Breite (m)</label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                className="quality-tile-input"
                                                value={editableFields.BREITE_ANH}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    handleFieldChange("BREITE_ANH", e.target.value)
                                                }
                                            />
                                        </div>

                                        <div className="quality-tile">
                                            <label className="quality-tile-label">Anhänger Höhe (m)</label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                className="quality-tile-input"
                                                value={editableFields.HOEHE_ANH}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    handleFieldChange("HOEHE_ANH", e.target.value)
                                                }
                                            />
                                        </div>

                                        {/* Anhänger Füllgrad - visuelle Darstellung */}
                                        <div className="quality-tile quality-tile-fillgrade">
                                            <label className="quality-tile-label">Anhänger Füllgrad (cm)</label>
                                            <div className="quality-fillgrade-display">
                                                <span className="quality-fillgrade-value">{editableFields.FGRAD_ANH} cm</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="-200"
                                                max="60"
                                                step="5"
                                                className="quality-fillgrade-slider"
                                                value={editableFields.FGRAD_ANH}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                    handleFieldChange("FGRAD_ANH", parseInt(e.target.value, 10))
                                                }
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Bereitstellungstabelle - nur für Waldholz */}
                                {(selectedDocument.fraction?.toUpperCase() === "WALDHOLZ") && (
                                    <div className="quality-staging-table-container">
                                        <div className="quality-staging-table-header">
                                            <h3 className="quality-staging-table-title">Bereitstellungen</h3>
                                            <div className="quality-staging-header-right">
                                                {stagingEntries.length > 0 && (
                                                    <div className={`quality-staging-distribution-sum ${stagingEntries.reduce((sum, entry) => sum + (entry.distribution || 0), 0) === 100
                                                        ? 'valid'
                                                        : 'invalid'
                                                        }`}>
                                                        Gesamt: {stagingEntries.reduce((sum, entry) => sum + (entry.distribution || 0), 0)}%
                                                        {stagingEntries.reduce((sum, entry) => sum + (entry.distribution || 0), 0) !== 100 &&
                                                            ' (muss 100% ergeben)'
                                                        }
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="quality-staging-add-btn"
                                                    onClick={addStagingEntry}
                                                >
                                                    + Neue Bereitstellung
                                                </button>
                                            </div>
                                        </div>
                                        <table className="quality-staging-table">
                                            <thead>
                                                <tr>
                                                    <th>Bereitstellungs-Nr.</th>
                                                    <th>Sortiment</th>
                                                    <th>Verteilung</th>
                                                    <th>Bereitstellung fertig</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stagingEntries.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="quality-staging-empty">
                                                            Keine Bereitstellungen vorhanden. Klicken Sie auf "Neue Bereitstellung" um eine hinzuzufügen.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    stagingEntries.map(entry => (
                                                        <tr key={entry.position}>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="quality-staging-input"
                                                                    value={entry.stagingNumber}
                                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                                        updateStagingEntry(entry.position, "stagingNumber", e.target.value)
                                                                    }
                                                                    placeholder="BS-Nummer"
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="quality-staging-input"
                                                                    value={entry.assortment}
                                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                                        updateStagingEntry(entry.position, "assortment", e.target.value)
                                                                    }
                                                                    placeholder="Sortiment"
                                                                />
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="quality-staging-select"
                                                                    value={entry.distribution}
                                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                                        updateStagingEntry(entry.position, "distribution", parseInt(e.target.value, 10))
                                                                    }
                                                                >
                                                                    <option value={5}>5%</option>
                                                                    <option value={10}>10%</option>
                                                                    <option value={15}>15%</option>
                                                                    <option value={20}>20%</option>
                                                                    <option value={25}>25%</option>
                                                                    <option value={30}>30%</option>
                                                                    <option value={35}>35%</option>
                                                                    <option value={40}>40%</option>
                                                                    <option value={45}>45%</option>
                                                                    <option value={50}>50%</option>
                                                                    <option value={55}>55%</option>
                                                                    <option value={60}>60%</option>
                                                                    <option value={65}>65%</option>
                                                                    <option value={70}>70%</option>
                                                                    <option value={75}>75%</option>
                                                                    <option value={80}>80%</option>
                                                                    <option value={85}>85%</option>
                                                                    <option value={90}>90%</option>
                                                                    <option value={95}>95%</option>
                                                                    <option value={100}>100%</option>
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className={`quality-staging-toggle ${entry.isCompleted ? "active" : ""}`}
                                                                    onClick={() =>
                                                                        updateStagingEntry(entry.position, "isCompleted", !entry.isCompleted)
                                                                    }
                                                                >
                                                                    {entry.isCompleted ? "JA" : "NEIN"}
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="quality-staging-delete-btn"
                                                                    onClick={() => deleteStagingEntry(entry.position)}
                                                                    title="Löschen"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Lieferschein Bemerkungen - Chat-Box Style (für alle Transporte) */}
                                <div className="quality-notes-container">
                                    <h3 className="quality-notes-title">Lieferschein Bemerkungen</h3>
                                    <div className="quality-notes-chat">
                                        {qaNotes.length === 0 ? (
                                            <div className="quality-notes-empty">
                                                Keine Bemerkungen vorhanden
                                            </div>
                                        ) : (
                                            qaNotes.map((note, index) => (
                                                <div key={index} className="quality-note-bubble">
                                                    <span className="quality-note-text">{note}</span>
                                                    <button
                                                        type="button"
                                                        className="quality-note-delete"
                                                        onClick={() => handleDeleteNote(index)}
                                                        title="Notiz löschen"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <div className="quality-notes-input-container">
                                        <input
                                            type="text"
                                            className="quality-notes-input"
                                            value={newNoteInput}
                                            maxLength={70}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => setNewNoteInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleAddNote();
                                                }
                                            }}
                                            placeholder="Neue Bemerkung eingeben (max. 70 Zeichen)..."
                                        />
                                        <button
                                            type="button"
                                            className="quality-notes-add-btn"
                                            onClick={handleAddNote}
                                        >
                                            Hinzufügen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="quality-empty-content">
                            <h2>Kein Transport ausgewählt</h2>
                            <p>Bitte wählen Sie einen Transport aus der Liste links aus.</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
